import { useState, useCallback } from "react"

import { Box, Typography, Accordion as MuiAccordion, type BoxProps } from "@mui/material"
import MuiAccordionSummary from "@mui/material/AccordionSummary"
import MuiAccordionDetails from "@mui/material/AccordionDetails"
import { styled } from "@mui/material/styles"

import { SvgIcon, type FC, WYSIWYG } from ".."

type AccordionProps = {
  items: {
    title: string
    text: string
  }[]
  startOpen?: boolean
}

const Accordion: FC<AccordionProps> = ({ items, startOpen = false }) => {
  const [expanded, setExpanded] = useState<string | false>(startOpen ? "panel0" : false)

  const handleChange = useCallback(
    (panel: string) => (_: unknown, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false)
    },
    []
  )

  return (
    <Box
      component="article"
      sx={{
        ...(expanded !== `panel${items.length - 1}` && {
          borderBottom: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
        }),
      }}
    >
      {items?.map((item, i) => (
        <MuiAccordion
          key={i}
          expanded={expanded === `panel${i}`}
          onChange={handleChange(`panel${i}`)}
          sx={{ borderRadius: "0 !important" }}
        >
          <MuiAccordionSummary
            expandIcon={
              <SvgIcon
                variant="arrow"
                size={16}
                sx={{
                  transform: "rotate(-90deg)",
                  fill: (theme) => theme.palette["neutral.700"].main,
                }}
              />
            }
            sx={{
              borderBottom: "0",
              borderTop: (theme) =>
                expanded === `panel${i}` || expanded === `panel${i - 1}`
                  ? "1px solid transparent"
                  : `1px solid ${theme.palette["neutral.200"].main}`,
            }}
          >
            <Typography variant="body.lg">{item.title}</Typography>
          </MuiAccordionSummary>
          <MuiAccordionDetails
            sx={{
              borderTop: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
            }}
          >
            <Box p={{ xs: 5, md: 15 }}>
              <WYSIWYG content={item.text} />
            </Box>
          </MuiAccordionDetails>
        </MuiAccordion>
      ))}
    </Box>
  )
}

export default Accordion

/** styled components */
const AccordionWrapper = styled((props: BoxProps) => <Box component="article" {...props} />)(
  ({ theme }) => `
  & > div:first-of-type {
    border-top: 1px solid ${theme.palette["neutral.200"].main}
  }
  `
)
